import { useCallback, useEffect, useRef } from 'react';

/**
 * Queues function calls until a condition is met, then flushes them in order.
 * Uses a ref for the function to ensure the latest version is called at flush time.
 *
 * Returns a dispatch function whose promise resolves with fn's return value.
 * When calls are queued, the returned promise resolves once fn actually runs
 * after the gate opens.
 */
export function useGatedDispatcher<TArgs extends unknown[], TReturn>(
    fn: (...args: TArgs) => TReturn | Promise<TReturn>,
    isReady: boolean
): (...args: TArgs) => Promise<TReturn> {
    const fnRef = useRef(fn);
    fnRef.current = fn;

    type Pending = {
        args: TArgs;
        resolve: (v: TReturn | PromiseLike<TReturn>) => void;
        reject: (e: unknown) => void;
    };
    const queue = useRef<Pending[]>([]);
    const readyRef = useRef(isReady);
    readyRef.current = isReady;

    const dispatch = useCallback((...args: TArgs): Promise<TReturn> => {
        /*
         ** Preserve FIFO: when isReady just flipped true but the flush effect
         ** hasn't run, new calls must wait behind already-queued ones.
         */
        if (readyRef.current && queue.current.length === 0) {
            return Promise.resolve(fnRef.current(...args));
        }
        return new Promise<TReturn>((resolve, reject) => {
            queue.current.push({ args, resolve, reject });
        });
    }, []);

    useEffect(() => {
        if (!isReady) return;
        const pending = queue.current.splice(0);
        pending.forEach(({ args, resolve, reject }) => {
            try {
                resolve(Promise.resolve(fnRef.current(...args)));
            } catch (e) {
                reject(e);
            }
        });
    }, [isReady]);

    return dispatch;
}
